import React, { useState } from "react";
import { useAuth } from "../Authentication/AuthContext";
import { subscribeToJobs } from "../../apis/UserApis";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { FaBell, FaTimes, FaCheckCircle, FaSpinner, FaPaperPlane, FaRocket } from "react-icons/fa";
import { HiLightningBolt, HiMail } from "react-icons/hi";

export default function JobSubscriptionBanner() {
  const { user, loggedInUser, setLoggedInUser } = useAuth();
  const { successToast, errorToast } = useToastHelpers();
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  // Don't show if not logged in, already subscribed, or dismissed
  if (!user || loggedInUser?.isSubscribe || dismissed || subscribed) {
    return null;
  }

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      await subscribeToJobs(user.userId, user.token);
      setLoggedInUser((prev) => ({ ...prev, isSubscribe: true }));
      setSubscribed(true);
      successToast("🎉 You're subscribed! You'll receive job alerts via email.");
    } catch (error) {
      console.error("Subscription error:", error);
      errorToast("Failed to subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Success state animation
  if (subscribed) {
    return (
      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.2),transparent_50%)]"></div>
        <div className="ca-container py-4">
          <div className="flex items-center justify-center gap-3 text-white">
            <FaCheckCircle className="w-5 h-5 animate-bounce" />
            <span className="font-semibold">🎉 Awesome! You're now subscribed to job alerts!</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500">
      {/* Animated mesh gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,255,255,0.3),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(255,200,100,0.3),transparent_50%)]"></div>
        {/* Animated shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-shimmer"></div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-2 left-8 text-white/20 animate-float-slow">
        <HiMail className="w-8 h-8" />
      </div>
      <div className="absolute bottom-2 right-12 text-white/15 animate-float-delayed">
        <FaRocket className="w-6 h-6 rotate-45" />
      </div>
      <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-yellow-300/50 rounded-full animate-ping-slow"></div>
      <div className="absolute top-3 right-1/3 w-1.5 h-1.5 bg-white/40 rounded-full animate-pulse"></div>

      <div className="relative ca-container py-3.5 sm:py-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          {/* Left content */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Animated icon container */}
            <div className="relative hidden sm:flex">
              <div className="absolute inset-0 bg-white/30 rounded-2xl blur-xl animate-pulse"></div>
              <div className="relative w-12 h-12 bg-white/20 backdrop-blur-sm border-2 border-white/40 rounded-2xl flex items-center justify-center shadow-lg">
                <FaBell className="w-5 h-5 text-white drop-shadow-lg animate-ring" />
                {/* Notification dot */}
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center animate-bounce">
                  <span className="text-[10px] text-white font-bold">!</span>
                </span>
              </div>
            </div>

            {/* Text content */}
            <div className="text-center sm:text-left">
              <div className="flex items-center gap-2 justify-center sm:justify-start">
                <span className="inline-flex items-center gap-1.5 bg-white/25 backdrop-blur-sm border border-white/30 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
                  <HiLightningBolt className="w-3 h-3 text-yellow-200" />
                  NEW
                </span>
                <h3 className="text-white font-bold text-base sm:text-lg drop-shadow-sm">
                  Get Job Alerts Straight to Your Inbox!
                </h3>
              </div>
              <p className="text-white/90 text-xs sm:text-sm mt-1 flex items-center gap-1.5 justify-center sm:justify-start">
                <FaPaperPlane className="w-3 h-3 hidden sm:inline" />
                Be the first to know when companies post new opportunities
              </p>
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="group relative inline-flex items-center gap-2 bg-white hover:bg-yellow-50 text-orange-600 font-bold py-2.5 sm:py-3 px-5 sm:px-6 rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none border-2 border-orange-200/50"
            >
              {loading ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  <span>Subscribing...</span>
                </>
              ) : (
                <>
                  <FaBell className="w-4 h-4 group-hover:animate-ring" />
                  <span>Subscribe Free</span>
                  <span className="hidden sm:inline-flex items-center justify-center bg-gradient-to-r from-orange-500 to-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full ml-1">
                    🔥 HOT
                  </span>
                </>
              )}
            </button>

            {/* Dismiss button */}
            <button
              onClick={() => setDismissed(true)}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200"
              aria-label="Dismiss"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes ring {
          0%, 100% { transform: rotate(0deg); }
          10% { transform: rotate(15deg); }
          20% { transform: rotate(-15deg); }
          30% { transform: rotate(10deg); }
          40% { transform: rotate(-10deg); }
          50% { transform: rotate(5deg); }
          60% { transform: rotate(-5deg); }
          70%, 100% { transform: rotate(0deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-8px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0) rotate(45deg); }
          50% { transform: translateY(-6px) rotate(50deg); }
        }
        @keyframes ping-slow {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.5); opacity: 0.5; }
        }
        .animate-ring {
          animation: ring 2s ease-in-out infinite;
        }
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
        .animate-float-slow {
          animation: float-slow 4s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 5s ease-in-out infinite 1s;
        }
        .animate-ping-slow {
          animation: ping-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}